import { useEffect, useState } from 'react';
import { Image, View } from 'react-native';

// Cap so a square/tall image (e.g. a 1:1 program icon used as the fallback when
// there's no hero photo) can't dominate the screen. The real landscape program
// photos (~3:2 / ~5:3) come in under this and render at their natural ratio,
// full-width and un-cropped.
const MAX_HEIGHT = 400;
// Until the natural size resolves (and if it fails to load), assume ~3:2 — the
// shape of the real hero photos — so the box doesn't jump on load.
const FALLBACK_ASPECT_RATIO = 3 / 2;

/**
 * Full-width program hero that adapts to the image's INTRINSIC dimensions (like
 * stylos's `width: 100%` <img>): we read the natural size and drive the box's
 * aspectRatio from it, so landscape photos AND square illustrations render
 * un-cropped and un-distorted. Height is capped at MAX_HEIGHT; an image taller
 * than that (e.g. a 1:1 icon) is letterboxed by `contain`, never cropped.
 */
export function ProgramHeroImage({ uri, alt }: { uri: string; alt: string }) {
  const [aspectRatio, setAspectRatio] = useState(FALLBACK_ASPECT_RATIO);

  useEffect(() => {
    let active = true;
    setAspectRatio(FALLBACK_ASPECT_RATIO);
    Image.getSize(
      uri,
      (width, height) => {
        if (active && height > 0) setAspectRatio(width / height);
      },
      // Keep the fallback ratio if the size can't be read.
      () => {}
    );
    return () => {
      active = false;
    };
  }, [uri]);

  return (
    <View className='w-full overflow-hidden rounded-vui-lg bg-background-50'>
      <Image
        source={{ uri }}
        resizeMode='contain'
        accessibilityLabel={alt}
        style={{ width: '100%', aspectRatio, maxHeight: MAX_HEIGHT }}
      />
    </View>
  );
}
